/**
 * Turning "do this work" into ROWS — one engine, for every type.
 *
 * WHAT A DISPATCH IS, and it was the same four steps everywhere: enumerate the units, open the step's progress run,
 * enqueue one row per unit, and resolve the twin when an enqueue deduped. That sequence was written out seven times
 * across three now-deleted modules (`setup-dispatch.ts`, `grading-dispatch.ts`, `refine-dispatch.ts`), and the ORDER
 * was the part
 * that kept being got wrong by hand: a run's record owns the `total`, the wording and the cleared unit set, so opening
 * it after the rows lets the step announce itself against the previous pass's total.
 *
 * A type declares its units (`batch` on the definition, see `BatchSpec`) and this runs them. There are no per-type
 * dispatchers left: the three modules held one wrapper per entry point, and every one of them turned out to be a
 * rename of this function — `dispatchAnswerExtraction(examId, ids)` for
 * `dispatchJobs('answerExtraction', examId, { submissionIds: ids })`. Call sites say the second form. What the wrappers
 * were also carrying, and where each thing went instead:
 *
 *  - the wait timeout, a constant per module → `batch.waitMs`, because how long a wait should be is a fact about the
 *    type and not about who is waiting,
 *  - `artifact === 'masterSolution' ? 'msRefine' : 'gsRefine'` → `REFINE_JOB_TYPE` and its two siblings in `job.ts`,
 *    beside `STEP_JOB_TYPES`,
 *  - reading the finished row's `note` → `dispatchJobsAndWait` returns it,
 *  - `{ jobIds, taskNumbers }` → `payloads`, which the caller maps. The engine cannot know that grading's callers want
 *    task numbers, and it does not have to.
 *
 * A CALL SITE NAMES ITS TYPE AS A STRING, deliberately, and that is what keeps this cheap. `*.job.ts` statically
 * imports its agents — that is what a handler IS — so a dispatch site importing the definition to reach it would drag
 * the whole agent graph in behind it, including into the client bundle (see the header of `run-in-worker.ts`). The
 * registry has already been armed by `db/index.ts` before any of this runs, so a string is all that is needed. The
 * cost is that a typo is a runtime error rather than a compile error; `dispatchJobs` logs it loudly and returns
 * nothing dispatched.
 *
 * EVERY GUARANTEE IS PER ROW — `lockKey` exclusion, `dedupeKey`, the lease, the retry count, the runner's fairness
 * accounting — and all of them are enforced by unique indexes on the document. That is why a round of 400 submissions
 * is 400 rows and not one row carrying 400 items: items in a subdocument array get none of it, and Mongo cannot lift
 * those indexes down to them (a `partialFilterExpression` cannot reference sibling state of an array element). The
 * fan-out has to stay.
 *
 * NOTHING GROUPS THE ROWS OF ONE DISPATCH. Each used to carry a `batchKey` naming its pass, which is what forced the
 * run-opening order to be threaded through every enqueue rather than just observed in one place. Completion is now a
 * question a row asks about the queue at its own terminal transition — see `step-completion.ts` — so the rows of a
 * pass need no label in common, and a dispatch is free to be nothing but a fan-out.
 */

import { logger } from '@adornis/base/logging.js';
import { openBatchKey, openOrJoinBatch, type BatchIntent } from './batch-store.js';
import { dedupeKeyFor, enqueueForType } from './enqueue.js';
import { findLiveJob, getJob, restampBatch } from './job-store.js';
import { waitForJobRows, type JobWaitOutcome } from './job-wait.js';
import { MAX_JOB_RUNTIME_MS } from './limits.js';
import { batchScopeOf, type DispatchScope, type JobPayload } from './job.js';
import { getJobDefinition } from './registry.js';

/**
 * What one dispatch created.
 *
 * `payloads` is returned alongside the ids, and it is not a convenience: a caller that has to NAME what it dispatched
 * — the re-grade announcement lists the main tasks it re-graded — would otherwise re-derive the enumeration the
 * engine just did, and the two would drift.
 */
/** The wait for a type that declares no `waitMs`: one full runtime budget, so the row has had its chance. */
const DEFAULT_WAIT_MS = MAX_JOB_RUNTIME_MS;

export interface DispatchResult<P extends JobPayload = JobPayload> {
  /** The rows doing the work — what a waiting caller waits ON. A deduped unit resolves to its twin, or drops out. */
  jobIds: string[];
  /** The units that got a row, in the same order the type enumerated them. */
  payloads: P[];
}

/**
 * Opens the run and enqueues one row per unit. Never throws.
 *
 * ENQUEUE FAILURES ARE LOGGED AND SWALLOWED, per unit, and that is deliberate rather than lax: every caller has
 * already committed its own state — the split IS confirmed, the upload HAS happened, the teacher DID ask — and has to
 * report that truthfully. A lost row costs latency, not work. A caller that needs to know uses the result.
 */
export async function dispatchJobs<P extends JobPayload = JobPayload>(
  type: string,
  examId: string,
  scope: DispatchScope = {},
  intent?: BatchIntent,
): Promise<DispatchResult<P>> {
  const empty: DispatchResult<P> = { jobIds: [], payloads: [] };
  if (!examId) return empty;

  const definition = getJobDefinition<P>(type);
  if (!definition?.batch) {
    logger.error({ type, examId }, 'Dispatch asked for a type that declares no batch');
    return empty;
  }
  const { units, joinTwin } = definition.batch;

  // ENUMERATED FIRST, AND A DISPATCH WITH NOTHING TO DO OPENS NOTHING. `openRun` still runs before any row exists —
  // which is the ordering that matters, see `BatchSpec.openRun` — but a run opened for zero units is worse than
  // useless: it sets the record IN_PROGRESS with a total and no row will ever arrive to close it, so the step is
  // wedged until something else opens a run over it. The deleted per-type dispatchers each guarded this with their own
  // `if (ids.length === 0) return` before opening; the guard belongs here, once.
  const payloads = await units(examId, scope);
  if (payloads.length === 0) {
    logger.info({ type, examId }, 'Dispatch enumerated no units — nothing queued, no run opened');
    return empty;
  }

  // The step OPENS here — after the units are known, before any row exists. Both halves matter: a run opened for zero
  // units can never be closed, and a run opened after its rows lets a row that finishes first try to close a step the
  // record still calls NOT_STARTED. See `runStepStart`.
  const start = definition.onStepStart;
  if (start) {
    const { runStepStart } = await import('./step-completion.js');
    await runStepStart(type, start, examId, scope);
  }

  // WHICH RUN THESE ROWS BELONG TO, minted or joined atomically — and joined is the common case, deliberately: one
  // upload changing three things is three dispatches and ONE wave, and the row that finishes last has to be able to
  // read all of it. See `openOrJoinBatch`.
  const batchKey = await openOrJoinBatch(examId, batchScopeOf(type));

  const enqueued = await Promise.all(
    payloads.map(async payload => {
      try {
        // THE INTENT TRAVELS WITH EVERY ROW, and on a dedupe the enqueue pushes it onto the row that absorbed it
        // (see `JobDoc.intents`). That is what keeps a trigger whose units all deduped from losing its reason —
        // it used to be recorded once on a batch document, which is the last thing that needed one.
        const job = await enqueueForType<P>(type, examId, payload, {
          batchKey,
          ...(intent ? { intent } : {}),
        });
        if (job) return { jobId: job._id, payload, inserted: true };

        // DEDUPED — equivalent work is already queued, which is an ordinary outcome. The row doing the work is the
        // row to wait for, so a type whose callers wait resolves the twin by the key that rejected this insert.
        //
        // For a type declaring `dedupeUntil: 'claimed'` the twin can only be a PENDING one, because a claimed row no
        // longer holds the key — which is the point: it has already read the state it grades against and cannot
        // stand in for a request made after that read. `findLiveJob` reads the same flag as the index, so the two
        // cannot disagree.
        if (!joinTwin) return null;
        const twin = await findLiveJob(dedupeKeyFor(definition, examId, payload));
        logger.info({ type, examId, twin: twin?._id ?? null }, 'Work already queued — joining it');
        return twin ? { jobId: twin._id, payload, inserted: false } : null;
      } catch (err) {
        logger.error({ err, type, examId }, 'Failed to enqueue a job');
        return null;
      }
    }),
  );

  const rows = enqueued.filter((row): row is DispatchedRow<P> => row !== null);
  const finalKey = await settleBatch(examId, batchScopeOf(type), batchKey, rows);
  logger.info({ type, examId, batchKey: finalKey, enqueued: rows.length, of: payloads.length }, 'Dispatched');
  return { jobIds: rows.map(row => row.jobId), payloads: rows.map(row => row.payload) };
}

interface DispatchedRow<P extends JobPayload> {
  jobId: string;
  payload: P;
  /** Whether this dispatch created the row, or resolved an existing twin. Only the former may be re-stamped. */
  inserted: boolean;
}

/**
 * Confirms the rows we just inserted are still in an OPEN batch, and moves them to one if they are not.
 *
 * THE WINDOW THIS CLOSES is between reading the batch key and inserting the rows. The key is read while some row of
 * the step is still live; if that row goes terminal in the interval and was the last one, the step drains, the wave
 * announces, and the batch key is cleared — and then our insert lands carrying a key belonging to a wave that is
 * already over. That row's own completion would later find its batch gone, read that as "somebody else is
 * announcing this wave", and stay silent. The work would be done and never reported.
 *
 * Note which way that fails: NOT a second announcement. The step cannot drain while any row of it is live, so a row
 * joining an open batch can only ever be folded into its one announcement. The hazard is an announcement too FEW.
 *
 * Ordinary and cheap: one indexed read per dispatch, and the repair path is reached only when the interval was
 * actually unlucky. Re-joining rather than always minting is deliberate — if a newer wave is already open, these
 * rows belong with the work still in flight rather than in a wave of their own.
 */
async function settleBatch<P extends JobPayload>(
  examId: string,
  step: string,
  batchKey: string,
  rows: Array<DispatchedRow<P>>,
): Promise<string> {
  const insertedIds = rows.filter(row => row.inserted).map(row => row.jobId);
  if (!insertedIds.length) return batchKey;
  if ((await openBatchKey(examId, step)) === batchKey) return batchKey;

  const replacement = await openOrJoinBatch(examId, step);
  const moved = await restampBatch(insertedIds, replacement);
  logger.info(
    { examId, step, from: batchKey, to: replacement, moved },
    'The batch closed while this dispatch was enqueueing — moved its new rows to the current one',
  );
  return replacement;
}

/**
 * Waits for rows this type produced, for the type's own `waitMs`.
 *
 * Split out from `dispatchJobsAndWait` for the one caller that has to answer before it waits: the grading tool names
 * the tasks it dispatched in its tool result, returns, and then waits in a detached block. Same wait, two moments.
 *
 * ON THE IDS, NOT ON THE TYPE. A caller holding a chat turn open asked for THESE rows; waiting on "every row of this
 * type for the exam" would also block on work someone else queued meanwhile and report its failure as this caller's.
 */
export async function waitForJobs(type: string, jobIds: string[], examId?: string): Promise<JobWaitOutcome> {
  // Nothing outstanding. `done` is the truthful answer and not an optimistic one: either the units were empty or
  // every enqueue collided with work that had already finished.
  if (jobIds.length === 0) return 'done';
  const waitMs = getJobDefinition(type)?.batch?.waitMs ?? DEFAULT_WAIT_MS;
  return waitForJobRows(jobIds, { type, ...(examId ? { examId } : {}) }, waitMs);
}

/**
 * Dispatch, then wait for exactly the rows it created.
 *
 * `note` is the finished row's own note, and only on `done` — where a handler says what happened BEYOND succeeding.
 * `gsRefine` uses it to tell the teacher, in the same turn, which tasks it could not create a grammar rule for. It is
 * the FIRST row's note, which is meaningful for the single-row types that wait and is why this is not a general
 * results channel: one string, written once, diagnostic by contract (`JobDoc.note`). Anything a later step needs
 * belongs in the domain write the handler already makes; anything a re-grade needs travels as a `RegradeIntent`.
 */
export async function dispatchJobsAndWait<P extends JobPayload = JobPayload>(
  type: string,
  examId: string,
  scope: DispatchScope = {},
  intent?: BatchIntent,
): Promise<DispatchResult<P> & { outcome: JobWaitOutcome; note?: string }> {
  const result = await dispatchJobs<P>(type, examId, scope, intent);
  const outcome = await waitForJobs(type, result.jobIds, examId);
  if (outcome !== 'done' || !result.jobIds[0]) return { ...result, outcome };

  // A gone row is not an error — it finished and was pruned.
  const row = await getJob(result.jobIds[0]);
  return { ...result, outcome, ...(row?.note ? { note: row.note } : {}) };
}
